import React, {useCallback, useEffect, useState} from 'react'
import {Form} from '@modifi/plugin-forms'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {useTranslation} from '@modifi/plugin-i18n'
import {Col, DangerAlert, H5, Row, SuccessAlert, ButtonLink, PreviousIcon} from '@modifi/ui'
import {generatePath} from '@modifi/router'

import {ROUTE_COMPANY_PROFILE_DIRECTORS} from '../../../../routes/constants'
import DirectorFormContent from '../components/director-form-content'
import PersonFormFooter from '../../components/person-form-footer'
import {FormContentWrapper} from '../../../../ui/form'
import {EntityFormData} from '../../../../types/common'
import {CompanyUnlockRefresher} from '../../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchDirectors} from '../../../../data/directors'
import {aFetchDocumentsByExternalPersonId} from '../../../../data/person-documents'

type ExistingDirectorProps = {
  isLoading: boolean
  updatePending: boolean
  initialize: (externalCompanyId: string, externalPersonId: string) => void
  externalCompanyId: string
  externalPersonId: string
  formData: EntityFormData
  onSubmit: (
    changes: Record<string, unknown>,
    externalCompanyId: string,
    externalPersonId: string
  ) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  redirect: (route: string) => void
  isValidPersonId: boolean
  pageIsVerified: boolean
  hasAnyRoleBasedFieldsVisible: boolean
  directorName: string
}

const ExistingDirector: React.FC<ExistingDirectorProps> = ({
  updatePending,
  externalPersonId,
  externalCompanyId,
  hasError,
  errorMessage,
  isLoading,
  onSubmit,
  initialize,
  formData,
  onDiscard,
  redirect,
  isValidPersonId,
  pageIsVerified,
  hasAnyRoleBasedFieldsVisible,
  directorName,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    initialize(externalCompanyId, externalPersonId)
  }, [initialize, externalCompanyId, externalPersonId])

  useEffect(() => {
    onDiscard()
  }, [onDiscard])

  useEffect(() => {
    if (!isValidPersonId) {
      redirect(generatePath(ROUTE_COMPANY_PROFILE_DIRECTORS))
    }
  }, [isValidPersonId, redirect, externalCompanyId])

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      if (currentPage === 1) {
        setCurrentPage(currentPage + 1)
      } else {
        onSubmit(changes, externalCompanyId, externalPersonId)
      }
    },
    [onSubmit, externalCompanyId, externalPersonId, currentPage]
  )

  const getSubmitButtonLabel = useCallback(() => {
    if (currentPage === 1) {
      return 'Next'
    }
    return t(`sub-section-footer.cta-submit`)
  }, [currentPage, t])

  const handleBackButtonClick = useCallback(() => {
    setCurrentPage(currentPage - 1)
  }, [currentPage])

  if (isLoading) {
    return null
  }

  return (
    <CustomerUploadManager>
      {pageIsVerified && <SuccessAlert>{t('director-page.fully-verified-alert')}</SuccessAlert>}

      {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}

      <Row>
        <Col>
          <ButtonLink
            data-target="director-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_DIRECTORS)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row>
        <Col mb={40}>
          <H5>{directorName}</H5>
        </Col>
      </Row>

      <Form
        isLoading={updatePending}
        defaultValues={formData.defaultValues}
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <FormContentWrapper>
          <DirectorFormContent
            t={t}
            isNewDirector={false}
            selectPersonAsDataSource={() => null}
            dataSourceOptions={[]}
            currentPage={currentPage}
            hasAnyRoleFieldItem={hasAnyRoleBasedFieldsVisible}
          />
        </FormContentWrapper>

        <CompanyUnlockRefresher
          refetch={(): any[] => [
            aFetchDirectors(),
            aFetchDocumentsByExternalPersonId(externalPersonId),
          ]}
          initialValues={formData.initialValues}
          defaultValues={formData.defaultValues}
        />
        <PersonFormFooter
          loading={updatePending}
          hasError={hasError}
          submitLabel={getSubmitButtonLabel()}
          canMoveBack={currentPage !== 1}
          handleBackButtonClick={handleBackButtonClick}
          isFinalStep={currentPage === 2}
        />
      </Form>
    </CustomerUploadManager>
  )
}

export default ExistingDirector
